import { Coverage, ProductClasType, ProductNameType } from "@repo/api";
import { useCallback } from "react";
import { capitalizeFirstLetter, formatDate, NumberToCZK, NumberToCZKWhole } from "../../helpers";
import { useTypedTranslation } from "../../hooks";
import { FufiCoverageCode } from "../../utils";

const isReleaseCoverageCode = (code: string | null | undefined) =>
    code === FufiCoverageCode.ReleaseCoverage || code === FufiCoverageCode.ZprnK1u;

export const formatFufiCoverageInsuredAmount = (
    coverage: Coverage,
    productClass: ProductClasType | undefined,
    emptyValue: string,
    productName?: ProductNameType | null,
): string => {
    if (productClass === ProductClasType.PseudoRsk && coverage.code === FufiCoverageCode.ReleaseCoverage) {
        return emptyValue;
    }

    if (productName === ProductNameType.NnFufiUl ? coverage.currentSumAssured != null : coverage.currentSumAssured) {
        return NumberToCZK(coverage.currentSumAssured);
    }

    return emptyValue;
};

export const formatFufiCoveragePremium = (
    coverage: Coverage,
    emptyValue: string,
    productName?: ProductNameType | null,
): string => {
    const hasPremium = productName === ProductNameType.NnFufiUl ? coverage.premium != null : Boolean(coverage.premium);
    return hasPremium ? NumberToCZKWhole(coverage.premium) : emptyValue;
};

export const useFufiCoverageNameFormatter = () => {
    const { t } = useTypedTranslation();

    return useCallback(
        (
            coverage: Coverage,
            isOwner?: boolean,
            productClass?: ProductClasType,
            productName?: ProductNameType | null,
        ): string => {
            if (productClass === ProductClasType.PseudoRsk && coverage.code === FufiCoverageCode.ReleaseCoverage) {
                return t("life.contractDetail.coverags.releaseCoverage");
            }
            if (productName === ProductNameType.NnFufiUl && isReleaseCoverageCode(coverage.code)) {
                return t("life.contractDetail.coverags.releaseCoverage");
            }
            const capitalizedName = capitalizeFirstLetter(coverage.name).trim();
            if (coverage.isMain && isOwner) {
                return `${t("life.contractDetail.coverags.mainCoverage")}: ${capitalizedName}`;
            }
            return capitalizedName;
        },
        [t],
    );
};

export const useFufiCoverageStartDateFormatter = () => {
    const { t } = useTypedTranslation();

    return useCallback(
        (dateStart: Date | null | undefined, insuranceTechnicalStartDate: Date | null | undefined) => {
            const start = dateStart ? new Date(dateStart) : null;
            const technicalStart = insuranceTechnicalStartDate ? new Date(insuranceTechnicalStartDate) : null;

            if (start && technicalStart && start < technicalStart) {
                // technical start date is later than coverage start date, show technical start date
                return formatDate(technicalStart, t("global.emptyValue"));
            }

            // coverage start date is later or there is no technical start date, show coverage start date
            return formatDate(start, t("global.emptyValue"));
        },
        [t],
    );
};
